import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  setDoc, 
  updateDoc, 
  deleteDoc,
  query, 
  where, 
  orderBy,
  addDoc,
  onSnapshot
} from 'firebase/firestore';
import { db, auth } from '../../lib/firebase';

export interface RouteSubmission {
  id?: string
  routeId: string
  clubId: string
  submittedBy: string
  submissionData: {
    name: string
    description: string
    routeType: string
    distance: number
    elevationGain: number
  }
  status: 'pending' | 'approved' | 'rejected'
  submittedAt?: Date
  reviewedAt?: Date | null
  reviewedBy?: string | null
  feedback?: string
}

// Create a route submission for club approval
export const createRouteSubmission = async (submission: Omit<RouteSubmission, 'id' | 'submittedAt'>): Promise<RouteSubmission | null> => {
  try {
    const submissionData = {
      ...submission,
      submittedAt: new Date(),
      reviewedAt: null,
      reviewedBy: null,
      feedback: ''
    }
    
    const docRef = await addDoc(collection(db, 'route_submissions'), submissionData)
    
    return {
      id: docRef.id,
      ...submissionData
    }
  } catch (error) {
    console.error('❌ Failed to create route submission:', error)
    return null
  }
}

// Get pending submissions for a club (for club admins)
export const getClubPendingSubmissions = async (clubId: string): Promise<RouteSubmission[]> => {
  try {
    const submissionsCollection = collection(db, 'route_submissions')
    const q = query(
      submissionsCollection,
      where('clubId', '==', clubId),
      where('status', '==', 'pending'),
      orderBy('submittedAt', 'desc')
    )
    const snapshot = await getDocs(q)
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      submittedAt: doc.data().submittedAt?.toDate(),
      reviewedAt: doc.data().reviewedAt?.toDate()
    })) as RouteSubmission[]
  } catch (error) {
    console.error('Failed to get club pending submissions:', error)
    return []
  }
}

// Get user's submissions
export const getUserSubmissions = async (userId: string): Promise<RouteSubmission[]> => {
  try {
    const submissionsCollection = collection(db, 'route_submissions')
    const q = query(
      submissionsCollection,
      where('submittedBy', '==', userId),
      orderBy('submittedAt', 'desc')
    )
    const snapshot = await getDocs(q)
    
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      submittedAt: doc.data().submittedAt?.toDate(),
      reviewedAt: doc.data().reviewedAt?.toDate()
    })) as RouteSubmission[]
  } catch (error) {
    console.error('Failed to get user submissions:', error)
    return []
  }
}

// Approve a submission (club admin only)
export const approveSubmission = async (submissionId: string, reviewerId: string): Promise<boolean> => {
  try {
    const submissionRef = doc(db, 'route_submissions', submissionId)
    
    // Get the submission to find the route
    const submissionDoc = await getDoc(submissionRef)
    if (!submissionDoc.exists()) {
      console.error('Submission not found')
      return false
    }
    
    const submission = submissionDoc.data()
    
    // Update submission
    await updateDoc(submissionRef, {
      status: 'approved',
      reviewedAt: new Date(),
      reviewedBy: reviewerId
    })
    
    // Update the route status to published while preserving submission data
    const routeRef = doc(db, 'routes', submission.routeId)
    
    // First fetch the current route to get existing submission data
    const routeDoc = await getDoc(routeRef)
    if (!routeDoc.exists()) {
      console.error('Route not found:', submission.routeId)
      return false
    }
    
    const routeData = routeDoc.data()
    
    // Update route with new status while preserving and enhancing submission data
    await updateDoc(routeRef, {
      status: 'published',
      type: 'published', // Ensure type is also set to published
      updatedAt: new Date(),
      // Preserve and enhance the submission data
      submission: {
        ...routeData.submission, // Keep original submission data (clubId, clubName, submittedAt)
        reviewedAt: new Date(),
        reviewedBy: reviewerId,
        status: 'approved'
      }
    })
    
    return true
  } catch (error) {
    console.error('❌ Failed to approve submission:', error)
    return false
  }
}

// Reject a submission (club admin only)
export const rejectSubmission = async (submissionId: string, reviewerId: string, feedback: string): Promise<boolean> => {
  try {
    const submissionRef = doc(db, 'route_submissions', submissionId)
    
    // Update submission
    await updateDoc(submissionRef, {
      status: 'rejected',
      reviewedAt: new Date(),
      reviewedBy: reviewerId,
      feedback: feedback
    })
    
    return true
  } catch (error) {
    console.error('❌ Failed to reject submission:', error)
    return false
  }
}

// Get pending submission counts for multiple clubs
export const getClubsPendingSubmissionCounts = async (clubIds: string[]): Promise<{ [clubId: string]: number }> => {
  try {
    const counts: { [clubId: string]: number } = {}
    
    // Initialize all clubs with 0
    clubIds.forEach(clubId => {
      counts[clubId] = 0
    })
    
    if (clubIds.length === 0) return counts
    
    // Firestore 'in' queries are limited to 10 items, so we need to batch
    const batchSize = 10
    const batches = []
    
    for (let i = 0; i < clubIds.length; i += batchSize) {
      batches.push(clubIds.slice(i, i + batchSize))
    }
    
    // Query each batch
    const submissionsCollection = collection(db, 'route_submissions')
    
    for (const batch of batches) {
      const q = query(
        submissionsCollection,
        where('clubId', 'in', batch),
        where('status', '==', 'pending')
      )
      const snapshot = await getDocs(q)
      
      // Count submissions per club
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        if (data.clubId && counts.hasOwnProperty(data.clubId)) {
          counts[data.clubId]++
        }
      })
    }
    
    return counts
  } catch (error) {
    console.error('Failed to get club pending submission counts:', error)
    return {}
  }
}

// Subscribe to pending submission count changes for multiple clubs
export const subscribeToClubsPendingSubmissions = (
  clubIds: string[], 
  onUpdate: (counts: { [clubId: string]: number }) => void
): (() => void) => {
  if (clubIds.length === 0) {
    onUpdate({})
    return () => {}
  }
  
  // Firestore 'in' queries are limited to 10 items, so we need to batch
  const batchSize = 10
  const batches = []
  const unsubscribes: (() => void)[] = []
  
  for (let i = 0; i < clubIds.length; i += batchSize) {
    batches.push(clubIds.slice(i, i + batchSize))
  }
  
  // Combined counts from all batches
  const allCounts: { [batchIndex: number]: { [clubId: string]: number } } = {}
  
  // Initialize all clubs with 0
  const initialCounts: { [clubId: string]: number } = {}
  clubIds.forEach(clubId => {
    initialCounts[clubId] = 0
  })
  
  // Subscribe to each batch
  batches.forEach((batch, batchIndex) => {
    const submissionsCollection = collection(db, 'route_submissions')
    const q = query(
      submissionsCollection,
      where('clubId', 'in', batch),
      where('status', '==', 'pending')
    )
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      // Count for this batch
      const batchCounts: { [clubId: string]: number } = {}
      batch.forEach(clubId => {
        batchCounts[clubId] = 0
      })
      
      snapshot.docs.forEach(doc => {
        const data = doc.data()
        if (data.clubId && batchCounts.hasOwnProperty(data.clubId)) {
          batchCounts[data.clubId]++
        }
      })
      
      // Store this batch's counts
      allCounts[batchIndex] = batchCounts
      
      // Combine all batch counts
      const combinedCounts = { ...initialCounts }
      Object.values(allCounts).forEach(batchCount => {
        Object.entries(batchCount).forEach(([clubId, count]) => {
          combinedCounts[clubId] = count
        })
      })
      
      onUpdate(combinedCounts)
    }, (error) => {
      console.error('Error listening to submission changes:', error)
      allCounts[batchIndex] = {}
      
      // Still update with combined counts from other batches
      const combinedCounts = { ...initialCounts }
      Object.values(allCounts).forEach(batchCount => {
        Object.entries(batchCount).forEach(([clubId, count]) => {
          combinedCounts[clubId] = count
        })
      })
      
      onUpdate(combinedCounts)
    })
    
    unsubscribes.push(unsubscribe)
  })
  
  // Return function that unsubscribes from all batches
  return () => {
    unsubscribes.forEach(unsubscribe => unsubscribe())
  }
}